<template>
  <div class="m-downloadtask-wrapper">
    <div class="m-downloadtask-container">
      <!-- Download list -->
      <div v-for="(file, index) in files" :key="file.id || index" class="download-item"
        :class="getDownloadStatusClass(file)">
        <div class="download-info">
          <i :class="getFileTypeIcon(file.fileName)"></i>
          <div class="download-details">
            <div class="download-filename">{{ file.fileName || 'Без имени' }}</div>
            <div class="download-meta">
              <span v-if="file.fileSize" class="download-size">
                {{ formatFileSize(file.fileSize) }}
              </span>
              <span v-if="file.progress !== undefined" class="download-progress">
                {{ file.progress }}%
              </span>
            </div>
          </div>
        </div>

        <div class="download-status">
          <ProgressBar v-if="file.status === 'downloading'" :value="file.progress || 0" :showValue="false"
            style="width: 100px" />
          <span v-else-if="file.status === 'completed'" class="status-completed">
            <i class="pi pi-check-circle"></i>
            Скачано
          </span>
          <span v-else-if="file.status === 'error'" class="status-error">
            <i class="pi pi-times-circle"></i>
            Ошибка
          </span>
          <span v-else class="status-pending">
            <i class="pi pi-clock"></i>
            Ожидание
          </span>
        </div>

        <Button v-if="file.status === 'completed'" icon="pi pi-folder-open" class="p-button-text p-button-sm"
          @click="handleOpenFile(file)" title="Открыть файл" />
      </div>

      <!-- Empty state -->
      <div v-if="files.length === 0" class="downloads-empty">
        <i class="pi pi-download"></i>
        <p>Нет файлов для загрузки</p>
      </div>
    </div>

    <!-- Action buttons -->
    <div v-if="files.length > 0" class="m-downloadtask-actions">
      <Button icon="pi pi-download" label="Скачать все" :disabled="disabled || allCompleted"
        @click="handleDownloadAll" />
      <Button icon="pi pi-trash" label="Очистить" class="p-button-outlined" @click="handleClear" />
    </div>

    <div v-if="hint" class="m-downloadtask-hint">
      {{ hint }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { getFileTypeIcon } from '@/common/utils/iconMapper'
import Button from 'primevue/button'
import ProgressBar from 'primevue/progressbar'
import { computed } from 'vue'

interface DownloadFile {
  id?: string | number
  fileName?: string
  fileUrl?: string
  fileSize?: number
  status?: 'pending' | 'downloading' | 'completed' | 'error'
  progress?: number
  error?: string
}

interface Props {
  modelValue?: DownloadFile[]
  disabled?: boolean
  hint?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  disabled: false,
  hint: ''
})

const emit = defineEmits<{
  'update:modelValue': [files: DownloadFile[]]
  'download-all': []
  'download-file': [file: DownloadFile]
  'open-file': [file: DownloadFile]
  'clear': []
}>()

const files = computed(() => {
  if (!props.modelValue) return []
  if (!Array.isArray(props.modelValue)) return []
  return props.modelValue
})

const allCompleted = computed(() =>
  files.value.length > 0 && files.value.every(f => f.status === 'completed')
)

const getDownloadStatusClass = (file: DownloadFile) => ({
  'download-completed': file.status === 'completed',
  'download-error': file.status === 'error',
  'download-downloading': file.status === 'downloading'
})

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

const handleDownloadAll = () => {
  emit('download-all')
}

const handleOpenFile = (file: DownloadFile) => {
  if (file.fileUrl) {
    window.open(file.fileUrl, '_blank')
  }
  emit('open-file', file)
}

const handleClear = () => {
  emit('clear')
  emit('update:modelValue', [])
}
</script>

<style scoped lang="scss">
.m-downloadtask-wrapper {
  width: 100%;
}

.m-downloadtask-container {
  width: 100%;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 1rem;
  max-height: 400px;
  overflow-y: auto;
}

.download-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }

  &.download-completed {
    background: #f0fdf4;
  }

  &.download-error {
    background: #fef2f2;
  }

  &.download-downloading {
    background: #eff6ff;
  }
}

.download-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;

  i {
    font-size: 1.5rem;
    color: #6b7280;
    flex-shrink: 0;
  }
}

.download-details {
  flex: 1;
  min-width: 0;
}

.download-filename {
  font-weight: 500;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.download-meta {
  display: flex;
  gap: 1rem;
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.download-status {
  flex-shrink: 0;
}

.status-completed {
  color: #10b981;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.status-error {
  color: #ef4444;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.status-pending {
  color: #6b7280;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.downloads-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #9ca3af;

  i {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  p {
    margin: 0;
  }
}

.m-downloadtask-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.m-downloadtask-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
}
</style>
